import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Computes time remaining locally from countdownStartTime + countdownDuration,
 * updating every second. When remaining goes to 0 or below, optionally
 * fetches the contract value once and uses it for display.
 *
 * Contract values are in seconds (Unix timestamp for start, seconds for duration).
 */
export function useLocalCountdown(
  countdownStartTime: bigint | null | undefined,
  countdownDuration: bigint | null | undefined,
  isCountdownActive: boolean,
  /** When expired, call to get the contract's current time remaining (e.g. 0 or negative). */
  getContractTimeRemaining?: () => Promise<bigint | null>,
) {
  const [timeRemaining, setTimeRemaining] = useState<bigint | null>(null)
  const expiredFetchedRef = useRef(false)
  const getContractRef = useRef(getContractTimeRemaining)
  getContractRef.current = getContractTimeRemaining

  const computeRemaining = useCallback((): bigint | null => {
    if (
      countdownStartTime == null ||
      countdownDuration == null ||
      !isCountdownActive
    ) {
      return null
    }
    const expiry = countdownStartTime + countdownDuration
    const nowSeconds = BigInt(Math.floor(Date.now() / 1000))
    const remaining = expiry - nowSeconds
    return remaining
  }, [countdownStartTime, countdownDuration, isCountdownActive])

  // Initial value and tick every second when countdown is active
  useEffect(() => {
    if (
      !isCountdownActive ||
      countdownStartTime == null ||
      countdownDuration == null
    ) {
      setTimeRemaining(null)
      expiredFetchedRef.current = false
      return
    }

    const tick = () => {
      const remaining = computeRemaining()
      if (remaining === null) return

      if (remaining <= 0n) {
        setTimeRemaining(remaining < 0n ? remaining : 0n)
        const fetchContract = getContractRef.current
        if (fetchContract && !expiredFetchedRef.current) {
          expiredFetchedRef.current = true
          fetchContract()
            .then((contractValue) => {
              setTimeRemaining(contractValue ?? 0n)
            })
            .catch(() => {})
        }
        return
      }

      setTimeRemaining(remaining)
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [
    isCountdownActive,
    countdownStartTime,
    countdownDuration,
    computeRemaining,
  ])

  return {
    timeRemaining,
    isExpired: timeRemaining !== null && timeRemaining <= 0n,
  }
}
